import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Users,
  TrendingUp,
  Gift,
  Calendar,
  RefreshCw,
  Shield,
  Zap,
  Rocket,
  Clock,
  Mail,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { adminKey } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats?admin_key=${adminKey}`);
      setStats(res.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError("Invalid admin key or failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) {
      fetchStats();
      // Auto-refresh every 60 seconds
      const interval = setInterval(fetchStats, 60000);
      return () => clearInterval(interval);
    }
  }, [adminKey]);

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p className="text-red-400">Access denied</p>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  const tierColors = {
    free: "text-indigo-400",
    pro: "text-indigo-400",
    growth: "text-indigo-400"
  };

  const tierIcons = {
    free: Shield,
    pro: Zap,
    growth: Rocket
  };

  return (
    <div className="min-h-screen bg-stone-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-indigo-500" />
              Admin Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Internal analytics for ClientKit
            </p>
          </div>
          <div className="flex items-center gap-4">
            {lastRefresh && (
              <span className="text-xs text-slate-400">
                Updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchStats}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-400 text-indigo-500 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-indigo-500">{stats?.users?.real_users || 0}</p>
                  <p className="text-sm text-indigo-400">Total Users</p>
                </div>
                <Users className="w-8 h-8 text-indigo-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-green-400">{stats?.signups?.today || 0}</p>
                  <p className="text-sm text-indigo-400">Signups Today</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-indigo-400">{stats?.signups?.this_week || 0}</p>
                  <p className="text-sm text-indigo-400">This Week</p>
                </div>
                <Calendar className="w-8 h-8 text-slate-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-indigo-400">{stats?.promo_codes?.total_redemptions || 0}</p>
                  <p className="text-sm text-indigo-400">Promo Redemptions</p>
                </div>
                <Gift className="w-8 h-8 text-indigo-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Users by Tier */}
          <Card className="bg-slate-900/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-lg">Users by Tier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats?.users?.by_tier || {}).map(([tier, count]) => {
                  const Icon = tierIcons[tier] || Shield;
                  const total = stats?.users?.real_users || 1;
                  const percent = Math.round((count / total) * 100);
                  return (
                    <div key={tier} className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${tierColors[tier]}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-indigo-500 capitalize font-medium">{tier}</span>
                          <span className="text-indigo-400">{count} ({percent}%)</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${tier === 'pro' ? 'bg-indigo-500' : tier === 'growth' ? 'bg-indigo-500' : 'bg-slate-600'}`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-indigo-400">Trial Users:</span>
                  <span className="text-indigo-500 ml-2">{stats?.users?.trial_users || 0}</span>
                </div>
                <div>
                  <span className="text-indigo-400">Promo Users:</span>
                  <span className="text-indigo-500 ml-2">{stats?.users?.promo_users || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Signups Chart */}
          <Card className="bg-slate-900/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-lg">Daily Signups (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between h-40 gap-2">
                {stats?.signups?.daily_breakdown?.map((day, i) => {
                  const maxCount = Math.max(...(stats?.signups?.daily_breakdown?.map(d => d.count) || [1]), 1);
                  const height = (day.count / maxCount) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <span className="text-xs text-indigo-400">{day.count}</span>
                      <div className="w-full bg-slate-800 rounded-t flex-1 relative" style={{ minHeight: '20px' }}>
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-indigo-500 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400">{day.day}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                <span className="text-indigo-400 text-sm">This Month: </span>
                <span className="text-indigo-500 font-semibold">{stats?.signups?.this_month || 0} signups</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Promo Codes */}
        <Card className="bg-slate-900/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-indigo-500 text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-indigo-400" />
              Promo Code Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {stats?.promo_codes?.codes?.map((code) => (
                <div key={code.code} className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-indigo-500 font-mono text-sm font-semibold">{code.code}</p>
                  <p className="text-xs text-indigo-400 capitalize">{code.tier}</p>
                  <p className="text-lg font-bold text-indigo-400 mt-1">{code.use_count || 0}</p>
                  <p className="text-xs text-slate-400">uses</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Signups */}
        <Card className="bg-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-indigo-500 text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-400" />
              Recent Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_signups?.length > 0 ? (
                stats.recent_signups.map((user, i) => {
                  const TierIcon = tierIcons[user.tier] || Shield;
                  return (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div>
                          <p className="text-indigo-500 font-medium">{user.name || 'Unknown'}</p>
                          <p className="text-sm text-indigo-400">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`flex items-center gap-1 ${tierColors[user.tier]}`}>
                          <TierIcon className="w-4 h-4" />
                          <span className="text-sm capitalize">{user.tier}</span>
                        </div>
                        {user.promo_code && (
                          <p className="text-xs text-indigo-400">🎟️ {user.promo_code}</p>
                        )}
                        <p className="text-xs text-slate-400">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-slate-400 text-center py-8">No signups yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-8">
          ClientKit Admin Dashboard • Auto-refreshes every 60 seconds
        </p>
      </div>
    </div>
  );
}
